import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, CircularProgress, Grid } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PaymentsIcon from "@mui/icons-material/PaymentsOutlined";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLongOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineOutlined";
import dayjs from "dayjs";
import { InvoiceApi, ProjectApi } from "../../../apis";
import { PageHeader, StatCard, DataView, EmptyState } from "../../../components/ui";
import { FilterBar, FilterSearch, FilterToggle } from "../../../components/ui/FilterBar";

const money = (value) =>
  `$${Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const BILLED_FILTERS = [
  { value: "all", label: "All" },
  { value: "billed", label: "Invoiced" },
  { value: "never", label: "Never invoiced" },
];

/**
 * One row per project: how much has been invoiced against it, and when it was
 * last invoiced.
 *
 * Every project is listed, including those never invoiced — those are the
 * point of the page, since an uninvoiced project is the one costing money.
 * There is no aggregate endpoint, so projects and invoices are fetched and
 * joined here; the invoice list alone could never show a project with no
 * invoices attached.
 */
export default function InvoiceSummary() {
  const [projects, setProjects] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [billed, setBilled] = useState("all");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [projectRes, invoiceRes] = await Promise.all([
        ProjectApi.getAllProjects(),
        InvoiceApi.getAllInvoices(),
      ]);

      const projectList =
        projectRes?.data?.data?.projects || projectRes?.data?.data || [];
      const invoiceData = invoiceRes?.data?.data || invoiceRes?.data || [];
      const invoiceList = Array.isArray(invoiceData)
        ? invoiceData
        : invoiceData.invoices || [];

      setProjects(Array.isArray(projectList) ? projectList : []);
      setInvoices(Array.isArray(invoiceList) ? invoiceList : []);
    } catch (error) {
      console.error("Failed to load invoice summary:", error);
      setProjects([]);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => {
    // Totals keyed by project so a project with no invoices still gets a row.
    const byProject = new Map();
    invoices.forEach((inv) => {
      const key = String(inv.projectId ?? inv.project?.id ?? "");
      if (!key) return;
      const entry = byProject.get(key) || { total: 0, count: 0, last: null };
      entry.total += Number(inv.totalAmount || 0);
      entry.count += 1;
      const date = inv.invoiceDate ? dayjs(inv.invoiceDate) : null;
      if (date && date.isValid() && (!entry.last || date.isAfter(entry.last))) {
        entry.last = date;
      }
      byProject.set(key, entry);
    });

    return projects
      .map((project) => {
        const agg = byProject.get(String(project.id)) || {
          total: 0,
          count: 0,
          last: null,
        };
        return {
          id: project.id,
          projectName: project.name || "—",
          clientName:
            project.client?.clientNickname || project.client?.company || "—",
          status: project.status || "—",
          invoiceCount: agg.count,
          totalInvoiced: agg.total,
          lastInvoiceDate: agg.last,
        };
      })
      .sort((a, b) => b.totalInvoiced - a.totalInvoiced);
  }, [projects, invoices]);

  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (billed === "billed" && row.invoiceCount === 0) return false;
      if (billed === "never" && row.invoiceCount > 0) return false;
      if (!term) return true;
      return (
        row.projectName.toLowerCase().includes(term) ||
        row.clientName.toLowerCase().includes(term)
      );
    });
  }, [rows, search, billed]);

  const summary = useMemo(
    () => ({
      total: rows.reduce((sum, r) => sum + r.totalInvoiced, 0),
      invoiceCount: rows.reduce((sum, r) => sum + r.invoiceCount, 0),
      neverInvoiced: rows.filter((r) => r.invoiceCount === 0).length,
    }),
    [rows]
  );

  const columns = useMemo(
    () => [
      { field: "projectName", headerName: "Project", sortable: true },
      { field: "clientName", headerName: "Client", sortable: true },
      {
        field: "invoiceCount",
        headerName: "Invoices",
        sortable: true,
        renderCell: ({ value }) => value || "—",
      },
      {
        field: "totalInvoiced",
        headerName: "Total invoiced",
        sortable: true,
        renderCell: ({ value }) => money(value),
      },
      {
        field: "lastInvoiceDate",
        headerName: "Last invoice",
        sortable: true,
        renderCell: ({ value }) =>
          value ? value.format("D MMM YYYY") : "Never invoiced",
      },
    ],
    []
  );

  const downloadCSV = () => {
    const header = ["Project", "Client", "Status", "Invoices", "Total invoiced", "Last invoice"];
    const lines = [header.join(",")];
    visibleRows.forEach((row) => {
      const values = [
        row.projectName,
        row.clientName,
        row.status,
        row.invoiceCount,
        row.totalInvoiced.toFixed(2),
        row.lastInvoiceDate ? row.lastInvoiceDate.format("YYYY-MM-DD") : "",
      ].map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`);
      lines.push(values.join(","));
    });

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `invoice-summary-${dayjs().format("YYYY-MM-DD")}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <PageHeader
        title="Invoice Summary"
        subtitle={`${rows.length} project${rows.length === 1 ? "" : "s"}`}
      />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <StatCard
                label="Total invoiced"
                value={money(summary.total)}
                icon={PaymentsIcon}
                color="primary"
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <StatCard
                label="Invoices"
                value={summary.invoiceCount}
                icon={ReceiptLongIcon}
                color="teal"
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <StatCard
                label="Never invoiced"
                value={summary.neverInvoiced}
                icon={ErrorOutlineIcon}
                color={summary.neverInvoiced ? "warning" : "violet"}
              />
            </Grid>
          </Grid>

          <FilterBar sx={{ mb: 2 }}>
            <FilterSearch
              value={search}
              onChange={setSearch}
              placeholder="Search project or client"
            />
            <FilterToggle value={billed} onChange={setBilled} options={BILLED_FILTERS} />
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="outlined"
              size="small"
              onClick={downloadCSV}
              disabled={!visibleRows.length}
              startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
              sx={{ height: 38 }}
            >
              Export CSV
            </Button>
          </FilterBar>

          <DataView
            rows={visibleRows}
            columns={columns}
            pageSize={10}
            empty={
              <EmptyState
                icon={ReceiptLongIcon}
                title={
                  billed === "never"
                    ? "Every project has been invoiced"
                    : "No matching projects"
                }
                description={
                  billed === "never"
                    ? "Nothing is waiting to be billed."
                    : "Try a different search term or filter."
                }
              />
            }
          />
        </>
      )}
    </Box>
  );
}
